import { getSitesApi } from '@/api-calls/sites/get'
import { HomeContainer } from '@/components/header/container'
import { LoadingContainer } from '@/components/header/loading'
import { getSiteDomain, getSiteUrlWithScheme } from '@/hooks/use-site'
import { Site } from '@blogging/prisma'
import { Button, Card, CardFooter, CardHeader, Separator } from '@blogging/ui'
import { useFetch, withQueryClientProvider } from '@ngtrvu/use-api'
import { ExternalLink, Pencil, Plus } from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'

function DashboardPageContent() {
  const navigate = useNavigate()
  const {
    data: sites,
    isLoading,
    isError,
  } = useFetch(getSitesApi, {
    resourceName: 'sites',
  })

  if (isLoading) {
    return <LoadingContainer />
  }

  if (isError) {
    return <div>Oops! Something went wrong.</div>
  }

  if (!sites || sites.length === 0) {
    // Check for pending invitation before redirecting to onboarding
    const pendingInvitation = localStorage.getItem('pending_invitation_token')
    console.log('[Dashboard] No sites found. Pending invitation:', pendingInvitation)

    if (pendingInvitation) {
      console.log('[Dashboard] Redirecting to invitation:', `/invitations/${pendingInvitation}`)
      navigate(`/invitations/${pendingInvitation}`)
    } else {
      console.log('[Dashboard] No pending invitation, redirecting to onboarding')
      navigate('/onboarding/new')
    }
    return
  }

  return (
    <HomeContainer>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {sites.map((site: Site) => {
          const siteDomain = getSiteDomain(site)
          const siteUrlWithScheme = getSiteUrlWithScheme(siteDomain)

          const siteRef = site.slug || site.id

          return (
            <Card key={site.id} className="border rounded-lg overflow-hidden">
              <Link to={`/${siteRef}`}>
                <CardHeader className="px-6 py-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <h2 className="text-lg font-medium truncate">
                        {site.name}
                      </h2>
                      <p className="text-gray-500 text-sm mt-1 truncate">
                        {siteDomain}
                      </p>
                    </div>
                    {site.logoIcon && (
                      <div className="flex-shrink-0 ml-3">
                        <div className="w-full h-12 rounded-lg overflow-hidden flex items-center justify-center">
                          <img
                            src={site.logoIcon}
                            alt={`${site.name} logo`}
                            className="w-12 h-12 object-cover"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </CardHeader>
              </Link>
              <Separator />
              <CardFooter className="px-6 py-4 flex justify-between">
                <div className="flex gap-2">
                  <Link to={`/${siteRef}/posts`}>
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex items-center gap-1"
                    >
                      <Pencil className="h-4 w-4 mr-1" />
                      View Posts
                    </Button>
                  </Link>
                </div>
                {siteUrlWithScheme && (
                  <a href={siteUrlWithScheme} target="_blank" rel="noreferrer">
                    <Button variant="ghost" size="sm" className="h-8">
                      View Site
                      <ExternalLink className="h-4 w-4 ml-1" />
                      <span className="sr-only">Open</span>
                    </Button>
                  </a>
                )}
              </CardFooter>
            </Card>
          )
        })}

        <Card className="border border-dashed rounded-lg flex flex-col px-6 py-4 h-full">
          <div className="flex flex-col">
            <h3 className="text-lg font-medium mb-2">Create a new site</h3>
            <p className="text-gray-500 text-sm mb-4">
              Start setting up your site and start writing and sharing your
              stories.
            </p>
          </div>
          <div className="flex justify-end">
            <Link to="/onboarding/new">
              <Button className="flex items-center gap-1">
                <Plus className="h-4 w-4" />
                Get Started
              </Button>
            </Link>
          </div>
        </Card>
      </div>
    </HomeContainer>
  )
}

export const DashboardPage = withQueryClientProvider(DashboardPageContent)
